import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";
import DataGrid, {
  Column,
  Button as ButtonGrid,
  Pager,
  FilterRow,
  RemoteOperations,
  Paging,
} from "devextreme-react/data-grid";
import { Popover } from "devextreme-react/popover";
import { Button } from "devextreme-react/button";
import PopUp, { ToolbarItem } from "devextreme-react/popup";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import { createStore } from "devextreme-aspnet-data-nojquery";
import {
  ResourceVersionHeaderInfoSVG,
  ResourceVersionActive,
  ResourceVersionInActive,
  ResourceCalenderSVG,
  ResourceVersionEditSVG,
  ResourceCountrySVG,
  ResourceOrgHierarchySVG,
  ResourceDeprecateSVG,
} from "../../Assets/SVGs/ResourceSVGs";
import Notification from "../Notification/Notification";
import {
  resourceVersionPropTypes,
  selectedResourcePropTypes,
} from "../../Constants/propTypes";
import "./OrganisationDetailsDataGrid.scss";
import ResourceVersionTooltip from "../ResourceVersionTooltip/ResourceVersionTooltip";
import { useUser } from "../../contexts/UserContext";
import { addOrgSvg } from "../../Assets/SVGs/organizationSVGs";
import SubscribeResourceToMultipleOrgs from "../SubscribeOrganizations/SubscribeResourceToMultipleOrgs";
import { appRequest } from "../../authentication/authConfig";
import { Roles } from "../../Constants/constants";

const deprecateInfoText =
  "Deprecating means you and your connected organizations will lose access to this version of the resource as well as any future updates to it.";
const deprecateQues = "Deprecate this resource version?";

const OrganisationDetailsDataGrid = ({
  selectedResource,
  selectedResourceVersion,
  setSelectedResourceCallback,
}) => {
  const { backend, userRoles } = useUser();
  const { instance, accounts } = useMsal();

  const [isPopoverVisible, setIsPopoverVisible] = useState(false);
  const [isDeprecatePopupVisible, setIsDeprecatePopupVisible] = useState(false);
  const [subscribedOrgs, setSubscribedOrgs] = useState(null);
  const [showaddOrgPopup, setShowaddOrgPopup] = useState(false);
  const [dataSource, setDataSource] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  const datagridRef = useRef(null);

  const details = selectedResourceVersion.resourceDetail;
  const canModifyResources = userRoles.includes(Roles.Resource_Manager);
  let clinithinkDateRange = {};

  useEffect(() => {
    setTotalCount(0);
    setSubscribedOrgs(null);
  }, [selectedResourceVersion]);

  useEffect(() => {
    const createDataSource = () =>
      createStore({
        key: "id",
        loadUrl: `/api/Resource/get/organizations-subscribed/by-resource-version?resourceVersionId=${selectedResourceVersion.id}`,
        onBeforeSend: async (method, ajaxOptions) => {
          // again some work around, ajaxOptions should always contain data.requireTotalCount
          if (typeof ajaxOptions.data !== "object") {
            // eslint-disable-next-line no-param-reassign
            ajaxOptions.data = {};
          }

          // Ensure requireTotalCount is always included and set to true
          // eslint-disable-next-line no-param-reassign
          ajaxOptions.data.requireTotalCount = true;
          try {
            const request = {
              ...appRequest,
              account: accounts[0],
            };
            const response = await instance.acquireTokenSilent(request);
            const { accessToken } = response;
            const xsrfToken = localStorage.getItem("csrf");

            // eslint-disable-next-line no-param-reassign
            ajaxOptions.headers = {
              "X-XSRF-TOKEN": xsrfToken,
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            };
          } catch (error) {
            console.error("Authorization Error: ", error);
          }
        },
        onLoaded: (result) => {
          // this is a work around as onLoaded would suggest that the datasource.IsLoading() func would return
          // false.. but in actual fact it returns true in here so wait a bit.. and yes I hate it.
          setTimeout(() => {
            setTotalCount(datagridRef.current.instance.getDataSource().totalCount());
            setSubscribedOrgs(result);
          }, 500);
        },
      });

    const newDataSource = createDataSource();
    setDataSource(newDataSource);
  }, [accounts, instance, selectedResourceVersion]);

  const showOrgPopup = useCallback(() => {
    setShowaddOrgPopup(true);
  }, []);

  const hideOrgPopup = useCallback(() => {
    setShowaddOrgPopup(false);
    const ds = datagridRef?.current?.instance?.getDataSource();
    ds.reload();
  }, []);

  const date = new Date(details?.releaseDate);

  const formattedDate = date.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });

  const resourceVersionDetailsPopover = useCallback(
    () =>
      ResourceVersionTooltip(
        selectedResource.organizationName,
        selectedResourceVersion.id,
        details
      ),
    [details, selectedResource.organizationName, selectedResourceVersion.id]
  );

  const handlePopoverClose = useCallback(() => {
    setIsPopoverVisible(false);
  }, []);

  const deprecateVersionPopup = useCallback(() => {
    setIsDeprecatePopupVisible(true);
  }, []);

  const popupHidden = () => {
    setIsDeprecatePopupVisible(false);
  };

  const deactivateSelectedResourceVersion = useCallback(() => {
    const updatedResourceVersions = selectedResource.resourceVersions.map((version) => {
      if (version.id === selectedResourceVersion.id) {
        return { ...version, active: false };
      }
      return version;
    });

    const updatedSelectedResource = {
      ...selectedResource,
      resourceVersions: updatedResourceVersions,
    };

    setSelectedResourceCallback(updatedSelectedResource);
  }, [selectedResource, selectedResourceVersion.id, setSelectedResourceCallback]);

  const handleConfirmDeprecateVersion = useCallback(() => {
    backend
      .callDeprecateResourceVersion(selectedResourceVersion.id)
      .then((res) => {
        if (res.succeeded) {
          Notification(res.message, "success");
          setIsDeprecatePopupVisible(false);
          deactivateSelectedResourceVersion();
        } else {
          Notification(res.message, "error");
          setIsDeprecatePopupVisible(false);
        }
      })
      .catch((res) => {
        console.error(res);
      });
  }, [backend, deactivateSelectedResourceVersion, selectedResourceVersion.id]);

  const getCancelOptions = useCallback(
    () => ({
      text: "Cancel",
      onClick: () => setIsDeprecatePopupVisible(false),
    }),
    []
  );

  const getDeprecateOptions = useCallback(
    () => ({
      text: "Yes, deprecate",
      onClick: handleConfirmDeprecateVersion,
    }),
    [handleConfirmDeprecateVersion]
  );

  const renderDeprecateResourcePopupContent = () => (
    <div className="text-align-center">
      <span className="deprecate-note">{deprecateInfoText}</span>
      <span className="deprecate-question">{deprecateQues}</span>
    </div>
  );

  const renderResourceVersionInfo = useCallback(
    () => (
      <span>
        {ResourceCalenderSVG} {formattedDate} | {ResourceCountrySVG}{" "}
        {selectedResourceVersion?.region?.toUpperCase() || "Unknown"} |{" "}
        {ResourceOrgHierarchySVG} {totalCount || 0} orgs subscribed |
        {selectedResourceVersion.active ? (
          <span className="text-success">{ResourceVersionActive} Active</span>
        ) : (
          <span className="text-danger">{ResourceVersionInActive} Deprecated</span>
        )}
      </span>
    ),
    [
      formattedDate,
      selectedResourceVersion.active,
      selectedResourceVersion?.region,
      totalCount,
    ]
  );

  const renderSelectedVersionDetails = useCallback(
    () => (
      <div className="org-header-text">
        <div>
          <PopUp
            visible={isDeprecatePopupVisible}
            hideOnOutsideClick={true}
            onHidden={popupHidden}
            showTitle={true}
            title="Deprecate resource"
            position="center"
            maxHeight={250}
            maxWidth={430}
            contentRender={renderDeprecateResourcePopupContent}
          >
            <ToolbarItem
              options={getCancelOptions()}
              widget="dxButton"
              location="after"
              toolbar="bottom"
              cssClass="cancel-resource-btn"
            />
            <ToolbarItem
              options={getDeprecateOptions()}
              widget="dxButton"
              location="after"
              toolbar="bottom"
              cssClass="create-resource-btn"
            />
          </PopUp>
          <span className="resource-version-name-wrapper">
            <span id="organisationdetailsdatagrid-span-resourcepopover">
              {selectedResource.name} - Version {selectedResourceVersion.versionNumber}
            </span>
            <Button
              className="resource-version-span-svg"
              onClick={() => {
                setIsPopoverVisible(true);
              }}
              stylingMode="text"
              hint="Show resource version details"
            >
              {ResourceVersionHeaderInfoSVG(isPopoverVisible)}
              <Popover
                className="resource-popover"
                target="#organisationdetailsdatagrid-span-resourcepopover"
                contentRender={resourceVersionDetailsPopover}
                width={484}
                visible={isPopoverVisible}
                onHiding={handlePopoverClose}
              />
            </Button>
          </span>
        </div>
        <div>
          <span className="resource-version-info-header">
            {renderResourceVersionInfo()}
          </span>
          {selectedResourceVersion.active && canModifyResources && (
            <span>
              <Button
                className="deprecate-version-span"
                onClick={() => deprecateVersionPopup()}
                stylingMode="text"
              >
                <span className="deprecate-version-svg">{ResourceDeprecateSVG}</span>
                <span>Deprecate version</span>
              </Button>
            </span>
          )}
        </div>
      </div>
    ),
    [
      canModifyResources,
      deprecateVersionPopup,
      getCancelOptions,
      getDeprecateOptions,
      handlePopoverClose,
      isDeprecatePopupVisible,
      isPopoverVisible,
      renderResourceVersionInfo,
      resourceVersionDetailsPopover,
      selectedResource.name,
      selectedResourceVersion.active,
      selectedResourceVersion.versionNumber,
    ]
  );
  const todayDate = useMemo(() => new Date(), []);

  const clinithinkItem = datagridRef?.current?.instance
    ?.getDataSource()
    ?.items()
    ?.find((item) => item.organizationName === "Clinithink");

  if (clinithinkItem) {
    if (clinithinkItem.subscriptionStart < todayDate) {
      clinithinkDateRange = {
        subscriptionStart: clinithinkItem?.todayDate,
        subscriptionEnd: clinithinkItem?.subscriptionEnd,
      };
    } else {
      clinithinkDateRange = {
        subscriptionStart: clinithinkItem?.subscriptionStart,
        subscriptionEnd: clinithinkItem?.subscriptionEnd,
      };
    }
  }

  return (
    <div className="org-datagrid">
      {renderSelectedVersionDetails()}
      <div className="datagrid-org-details">
        <div className="resource-subscription-details">
          <span className="datagrid-text">
            <em>View and manage active subscriptions of resources</em>
          </span>
          {canModifyResources && (
            <Button
              onClick={showOrgPopup}
              className="add-org"
              type="normal"
              stylingMode="text"
            >
              {addOrgSvg}
              <span className="add-org-label">Add Organizations</span>
            </Button>
          )}
        </div>
        <DataGrid
          height="96%"
          ref={datagridRef}
          dataSource={dataSource}
          hoverStateEnabled={true}
          showBorders={true}
          noDataText="No active subscriptions available"
          key={selectedResourceVersion.id}
        >
          <RemoteOperations sorting paging filtering grouping />
          <Pager
            visible={true}
            displayMode="full"
            allowedPageSizes={[10, 50, 100]}
            showPageSizeSelector={true}
          />
          <Paging defaultPageSize={50} />
          <FilterRow visible={false} />
          <Column
            dataField="organizationName"
            caption="Organization name"
            width={250}
            allowSorting={false}
          />
          <Column dataField="createdBy" caption="Created by" />
          <Column
            dataField="subscriptionStart"
            dataType="date"
            format="MMM dd, yyyy"
            caption="Subscription start date"
            filterType="date"
          />
          <Column
            dataField="subscriptionEnd"
            dataType="date"
            format="MMM dd, yyyy"
            caption="Subscription end date"
            filterType=""
          />
          <Column caption="Action" type="buttons" name="resViewButton" visible={null}>
            <ButtonGrid>
              <span>
                <span>{ResourceVersionEditSVG}</span>
              </span>
            </ButtonGrid>
          </Column>
        </DataGrid>
      </div>
      {showaddOrgPopup && (
        <SubscribeResourceToMultipleOrgs
          visible={showaddOrgPopup}
          onHidingCallback={hideOrgPopup}
          resourceVersionDisplay={`${selectedResource?.name} - Version ${selectedResourceVersion?.versionNumber}`}
          resourceVersionId={selectedResourceVersion.id}
          currentlySubscribedOrgs={subscribedOrgs}
          clinithinkSubscriptionRange={clinithinkDateRange}
        />
      )}
    </div>
  );
};

OrganisationDetailsDataGrid.propTypes = {
  selectedResource: selectedResourcePropTypes,
  selectedResourceVersion: resourceVersionPropTypes,
  setSelectedResourceCallback: PropTypes.func.isRequired,
};
export default OrganisationDetailsDataGrid;
